import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { ShieldStar, Plus, PencilSimple, Trash, EnvelopeSimple, Lock, User as UserIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SuperAdminsPanel() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [meEmail, setMeEmail] = useState("");
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null); // {id, email, name}

    const load = async () => {
        setLoading(true);
        try {
            const [{ data }, meRes] = await Promise.all([
                api.get("/admin/super-admins"),
                api.get("/auth/me").catch(() => null),
            ]);
            setAdmins(data.admins || []);
            setMeEmail(meRes?.data?.email || "");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not load admins"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const remove = async (a) => {
        if (a.email === meEmail) { toast.error("You can't delete your own account while signed in"); return; }
        if (!window.confirm(`Delete super-admin ${a.email}? This can't be undone.`)) return;
        try { await api.delete(`/admin/super-admins/${a.id}`); toast.success("Administrator removed"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="space-y-4" data-testid="super-admins-panel">
            <Card className="p-4 bg-white border-slate-200">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                        <ShieldStar size={20} weight="fill" className="text-civic-700" />
                        <div>
                            <h2 className="font-heading font-bold text-base text-slate-900">Super administrators</h2>
                            <p className="text-xs text-slate-500 leading-snug">Accounts with full access to the Admin Console. Change email + password below.</p>
                        </div>
                    </div>
                    <Button onClick={() => setCreating(true)} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="add-super-admin-btn">
                        <Plus size={14} className="mr-1" weight="bold" /> Add
                    </Button>
                </div>

                {admins.length === 0 ? (
                    <div className="text-sm text-slate-500 italic p-4 text-center">{loading ? "Loading…" : "No administrators. Add one above."}</div>
                ) : (
                    <div className="divide-y divide-slate-100" data-testid="super-admins-list">
                        {admins.map((a) => (
                            <div key={a.id} className="py-2.5 flex items-center gap-3" data-testid={`super-admin-row-${a.id}`}>
                                <span className="h-8 w-8 grid place-items-center rounded-md bg-civic-50 border border-civic-100 shrink-0">
                                    <UserIcon size={14} weight="duotone" className="text-civic-700" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-slate-900 truncate">{a.name || "System Administrator"}</div>
                                    <div className="hash-mono text-[11px] text-slate-500 truncate flex items-center gap-1">
                                        <EnvelopeSimple size={10} /> {a.email}
                                        {a.email === meEmail && <span className="ml-2 px-1.5 py-0.5 rounded bg-civic-100 text-civic-700 text-[9px] font-semibold uppercase tracking-wider">You</span>}
                                    </div>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => setEditing(a)} className="h-8" data-testid={`edit-super-admin-${a.id}`}>
                                    <PencilSimple size={12} className="mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => remove(a)} disabled={a.email === meEmail || admins.length <= 1} className="h-8 text-rose-700 border-rose-200 hover:bg-rose-50 disabled:opacity-40" data-testid={`delete-super-admin-${a.id}`}>
                                    <Trash size={12} />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <SuperAdminDialog
                open={creating}
                onOpenChange={setCreating}
                mode="create"
                onSaved={() => { setCreating(false); load(); }}
            />
            <SuperAdminDialog
                open={!!editing}
                onOpenChange={(o) => !o && setEditing(null)}
                mode="edit"
                initial={editing}
                onSaved={() => { setEditing(null); load(); }}
            />
        </div>
    );
}

function SuperAdminDialog({ open, onOpenChange, mode, initial, onSaved }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(initial?.name || "");
        setEmail(initial?.email || "");
        setPassword("");
    }, [open, initial]);

    const submit = async () => {
        if (mode === "create" && (!email.trim() || !password)) {
            toast.error("Email and password are required"); return;
        }
        setSaving(true);
        try {
            if (mode === "create") {
                await api.post("/admin/super-admins", { email: email.trim(), password, name: name.trim() || undefined });
                toast.success("Administrator added");
            } else {
                const payload = {};
                if (name.trim() && name.trim() !== initial?.name) payload.name = name.trim();
                if (email.trim() && email.trim() !== initial?.email) payload.email = email.trim();
                if (password) payload.password = password;
                if (Object.keys(payload).length === 0) { toast.error("No changes to save"); setSaving(false); return; }
                await api.put(`/admin/super-admins/${initial.id}`, payload);
                toast.success("Administrator updated");
            }
            onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="super-admin-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">{mode === "create" ? "Add super administrator" : "Edit super administrator"}</DialogTitle>
                    <DialogDescription>{mode === "create" ? "Give the new admin an email and initial password (≥8 chars)." : "Leave the password blank to keep it unchanged."}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="overline text-slate-500">Name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="System Administrator" data-testid="super-admin-name" />
                    </div>
                    <div>
                        <Label className="overline text-slate-500">Email</Label>
                        <div className="relative">
                            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" className="pl-9" data-testid="super-admin-email" />
                            <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        </div>
                    </div>
                    <div>
                        <Label className="overline text-slate-500">Password {mode === "edit" && <span className="text-slate-400 normal-case">(blank = keep unchanged)</span>}</Label>
                        <div className="relative">
                            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters" className="pl-9 font-mono" data-testid="super-admin-password" />
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="super-admin-save">
                        {saving ? "Saving…" : (mode === "create" ? "Add admin" : "Save changes")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
